/**
 * My Quotes — installer hub.
 *
 * Three big tiles:
 *   • Start a New Quote → /quote-project (the existing project-quote builder)
 *   • Saved Quotes      → /my-quotes/saved (status=draft, scoped to caller)
 *   • Sent Quotes       → /my-quotes/sent  (status in {sent,accepted,declined})
 *
 * Replaces the old "Quote a Project" entry that took users straight into the
 * builder. Now users land on a chooser so they can resume a saved draft or
 * review what they've already sent.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, FilePlus2, FolderOpen, Send } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const TILES = [
  {
    id: 'new',
    title: 'Start a New Quote',
    description: 'Build a customer-facing project quote from scratch.',
    icon: FilePlus2,
    path: '/quote-project',
    accent: '#2F5E96',
  },
  {
    id: 'saved',
    title: 'Saved Quotes',
    description: 'Resume drafts you haven’t sent to the customer yet.',
    icon: FolderOpen,
    path: '/my-quotes/saved',
    accent: '#D4A24B',
  },
  {
    id: 'sent',
    title: 'Sent Quotes',
    description: 'View every quote you’ve already emailed to a customer.',
    icon: Send,
    path: '/my-quotes/sent',
    accent: '#1f7a4a',
  },
];

export default function MyQuotes() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50">
      <div
        className="text-white"
        style={{
          background: 'linear-gradient(135deg,#0B0B0E 0%,#1f4030 75%)',
          borderBottom: '3px solid #D4A24B',
        }}
      >
        <div className="max-w-[1100px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => navigate('/probound')}
            data-testid="myquotes-back"
            className="p-2 hover:bg-white/10 rounded-lg shrink-0"
            aria-label="Back to ProBound"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              Customer Quotes
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">
              Start a new project quote, resume a draft, or review what you’ve sent.
            </p>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3"
          >
            <Home className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-3 sm:px-4 py-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => navigate(t.path)}
              data-testid={`myquotes-${t.id}`}
              className="text-left"
            >
              <Card className="p-5 h-full hover:shadow-lg transition-all border-2 hover:border-gray-300">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                  style={{ background: t.accent + '22', color: t.accent }}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <h2 className="text-base font-bold text-gray-900 mb-1">{t.title}</h2>
                <p className="text-xs text-gray-500">{t.description}</p>
              </Card>
            </button>
          );
        })}
      </div>
    </div>
  );
}
